import React from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

// Helper to round ISO string to minute precision and return local time string
function toMinutePrecisionLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  d.setSeconds(0, 0);
  // Return a string that represents local time for charting (e.g. 'YYYY-MM-DD HH:mm')
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0") +
    " " +
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0")
  );
}

// Helper to format date as DD.MM.YY (local time)
function formatDateDDMMYY(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
}

// Helper to format date as DD.MM.YY HH:mm (local time)
function formatDateTimeDDMMYYHM(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);
  const hour = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${day}.${month}.${year} ${hour}:${min}`;
}

// Helper to format date as HH:mm (local time)
function formatTimeHM(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const hour = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${hour}:${min}`;
}

function SensorHistoryGraph({
  allData,
  timeframe,
  customFrom,
  customTo,
  raw
}) {
  // Defensive: ensure allData is always an array
  const safeAllData = Array.isArray(allData) ? allData : [];

  // Helper to check if custom range is <= 24h
  function isCustomRangeShort() {
    if (timeframe !== "custom" || !customFrom || !customTo) return false;
    const from = new Date(`${customFrom}T00:00:00`);
    const to = new Date(`${customTo}T23:59:59`);
    return (to - from) <= 24 * 60 * 60 * 1000;
  }

  // Compute min/max for all sensors
  const values = safeAllData.flatMap(row =>
    Array.isArray(row.data) ? row.data.map(Number) : []
  );
  const dataMin = values.length ? Math.min(...values) : 0;
  const dataMax = values.length ? Math.max(...values) : 3000;
  let yMin;
  let yMax;
  if (raw) {
    yMin = Math.floor(dataMin / 100) * 100;
    yMax = Math.ceil(dataMax / 100) * 100;
  } else {
    yMin = Math.floor(dataMin / 10) * 10;
    yMax = Math.ceil(dataMax / 10) * 10;
  }


  // Prevent rendering chart if no data
  if (!safeAllData.length) {
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span>No data available</span>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={safeAllData.map(row => {
          const t = toMinutePrecisionLocal(row.time);
          // Adapt to new schema: row.data is [sensor1, sensor2, sensor3]
          return {
            time: t,
            sensor1: row.data?.[0] != null ? Number(row.data[0]) : null,
            sensor2: row.data?.[1] != null ? Number(row.data[1]) : null,
            sensor3: row.data?.[2] != null ? Number(row.data[2]) : null,
            _utc: row.time
          };
        })}
        margin={{ top: 24, right: 30, left: 0, bottom: 10 }} // reduce top margin, handled by custom legend
      >
        <Legend
          verticalAlign="top"
          align="center"
          content={props => (
            <div style={{ marginBottom: 32 }}>
              <ul style={{ display: 'flex', justifyContent: 'center', padding: 0, margin: 0, listStyle: 'none' }}>
                {props.payload.map((entry, index) => (
                  <li key={`item-${index}`} style={{ margin: '0 16px', color: entry.color }}>
                    <svg width="14" height="14" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                      <rect width="14" height="14" fill={entry.color} />
                    </svg>
                    <span style={{ verticalAlign: 'middle' }}>{entry.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        />
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="time"
          minTickGap={40}
          tickFormatter={
            (timeframe === "day") ||
            (timeframe === "custom" && isCustomRangeShort())
              ? (localTimeStr, idx, allTicks) => {
                  // Parse local time string back to Date for formatting
                  const [datePart, timePart] = localTimeStr.split(" ");
                  const [year, month, day] = datePart.split("-");
                  const [hour, min] = timePart.split(":");
                  const d = new Date(
                    Number(year),
                    Number(month) - 1,
                    Number(day),
                    Number(hour),
                    Number(min)
                  );
                  return `${formatDateDDMMYY(d)}\n${formatTimeHM(d)}`;
                }
              : (localTimeStr) => {
                  const [datePart, timePart] = localTimeStr.split(" ");
                  const [year, month, day] = datePart.split("-");
                  const [hour, min] = timePart.split(":");
                  const d = new Date(
                    Number(year),
                    Number(month) - 1,
                    Number(day),
                    Number(hour),
                    Number(min)
                  );
                  return formatDateDDMMYY(d);
                }
          }
          tick={({ x, y, payload }) => {
            const showTime =
              (timeframe === "day") ||
              (timeframe === "custom" && isCustomRangeShort());
            const localTimeStr = payload.value;
            const [datePart, timePart] = localTimeStr.split(" ");
            const [year, month, day] = datePart.split("-");
            const [hour, min] = timePart.split(":");
            const d = new Date(
              Number(year),
              Number(month) - 1,
              Number(day),
              Number(hour),
              Number(min)
            );
            if (!showTime) {
              return (
                <text
                  x={x}
                  y={y + 16}
                  textAnchor="middle"
                  fill="#666"
                  fontSize={14}
                >
                  {formatDateDDMMYY(d)}
                </text>
              );
            }
            return (
              <g>
                <text
                  x={x}
                  y={y + 10}
                  textAnchor="middle"
                  fill="#666"
                  fontSize={14}
                >
                  {formatDateDDMMYY(d)}
                </text>
                <text
                  x={x}
                  y={y + 24}
                  textAnchor="middle"
                  fill="#888"
                  fontSize={14}
                >
                  {formatTimeHM(d)}
                </text>
              </g>
            );
          }}
        />
        <YAxis
          domain={[yMin, yMax]}
        />
        <Tooltip
          labelFormatter={(_, payload) => {
            // Use original UTC time for tooltip, but display in local time
            if (payload && payload.length && payload[0].payload && payload[0].payload._utc) {
              return formatDateTimeDDMMYYHM(payload[0].payload._utc);
            }
            return "";
          }}
        />
        <Line type="monotone" dataKey="sensor1" stroke="#8884d8" name="1" dot={false} />
        <Line type="monotone" dataKey="sensor2" stroke="#82ca9d" name="2" dot={false} />
        <Line type="monotone" dataKey="sensor3" stroke="#ff7300" name="3" dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default SensorHistoryGraph;
